import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, BarChart3, Send, Download } from "lucide-react";
import { api } from "../../lib/api";

const lines = (v) => String(v || "").split("\n").map((x) => x.trim()).filter(Boolean);

function Bar({ label, count, pct }) {
  return (
    <div className="space-y-1" data-testid={`result-bar-${label}`}>
      <div className="flex justify-between text-xs text-navy/60"><span className="truncate pr-2">{label}</span><span className="shrink-0 font-medium text-navy/80">{count} · {pct}%</span></div>
      <div className="h-2.5 rounded-full bg-navy/10 overflow-hidden"><div className="h-full rounded-full bg-violet transition-all" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export default function ModuleResponses({ moduleKey, item, kind }) {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [poll, setPoll] = useState("");
  const [ratings, setRatings] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const opts = lines(item.opciones);
  const questions = lines(item.preguntas);
  const formFields = lines(item.campos);

  const load = () => {
    setLoading(true);
    api.get(`/mod/${moduleKey}/items/${item.id}/results`)
      .then((r) => {
        setResults(r.data);
        const m = r.data.mine;
        if (kind === "poll" && m?.option) setPoll(m.option);
        if (kind === "survey" && m?.ratings) setRatings(m.ratings);
        if (kind === "form" && m?.values) setValues(m.values);
      })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [moduleKey, item.id]); // eslint-disable-line

  const submit = async () => {
    let answers;
    if (kind === "poll") {
      if (!poll) return toast.error("Elige una opción");
      answers = { option: poll };
    } else if (kind === "form") {
      if (Object.values(values).every((v) => !String(v || "").trim())) return toast.error("Completa al menos un campo");
      answers = { values };
    } else {
      if (Object.keys(ratings).length === 0) return toast.error("Responde al menos una pregunta");
      answers = { ratings };
    }
    setSaving(true);
    try { await api.post(`/mod/${moduleKey}/items/${item.id}/respond`, { answers }); toast.success("¡Gracias por responder!"); load(); }
    catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/mod/${moduleKey}/items/${item.id}/responses.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(item.titulo || "formulario").replace(/\s+/g, "_")}_respuestas.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar el PDF"); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-4" data-testid="module-responses">
      {/* Responder */}
      <div className="space-y-3">
        <div className="overline text-navy/40">{kind === "form" ? "Llenar formulario" : "Responder"}</div>
        {kind === "poll" ? (
          <div className="space-y-2">
            {opts.length === 0 && <p className="text-sm text-navy/40">Esta app aún no tiene opciones.</p>}
            {opts.map((o) => (
              <label key={o} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${poll === o ? "border-violet bg-violet/5" : "border-navy/10 hover:border-navy/20"}`} data-testid={`poll-opt-${o}`}>
                <input type="radio" name="poll" value={o} checked={poll === o} onChange={() => setPoll(o)} className="accent-violet" />
                {o}
              </label>
            ))}
          </div>
        ) : kind === "form" ? (
          <div className="space-y-3">
            {formFields.length === 0 && <p className="text-sm text-navy/40">Este formulario aún no tiene campos.</p>}
            {formFields.map((c, i) => (
              <div key={i} data-testid={`form-field-${i}`}>
                <label className="text-xs text-navy/50 mb-1 block">{c}</label>
                <input className="w-full bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet text-sm"
                  value={values[i] || ""} onChange={(e) => setValues({ ...values, [i]: e.target.value })}
                  data-testid={`form-input-${i}`} />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {questions.length === 0 && <p className="text-sm text-navy/40">Esta encuesta aún no tiene preguntas.</p>}
            {questions.map((q, i) => (
              <div key={i} data-testid={`survey-q-${i}`}>
                <div className="text-sm text-navy/70 mb-1">{q}</div>
                <div className="flex gap-1.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setRatings({ ...ratings, [i]: n })}
                      className={`w-9 h-9 rounded-lg text-sm font-medium border transition-colors ${Number(ratings[i]) === n ? "border-violet bg-violet text-white" : "border-navy/10 text-navy/50 hover:border-navy/30"}`}
                      data-testid={`survey-q-${i}-rate-${n}`}>{n}</button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="module-respond-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {results?.mine ? (kind === "form" ? "Actualizar envío" : "Actualizar respuesta") : (kind === "form" ? "Enviar formulario" : "Enviar respuesta")}
        </button>
      </div>

      {/* Resultados en vivo */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 overline text-navy/40"><BarChart3 className="w-3.5 h-3.5" /> {kind === "form" ? "Resumen en vivo" : "Resultados en vivo"} · {results?.total ?? 0} {kind === "form" ? "envíos" : "respuestas"}
          {kind === "form" && (results?.total ?? 0) > 0 && <button onClick={downloadPdf} className="btn btn-outline text-xs py-1 ml-auto normal-case" data-testid="form-download-pdf"><Download className="w-3.5 h-3.5" /> Descargar PDF</button>}
        </div>
        {loading ? <div className="py-4 flex justify-center text-navy/40"><Loader2 className="w-5 h-5 animate-spin" /></div> :
          !results || results.total === 0 ? <p className="text-sm text-navy/40" data-testid="results-empty">Aún no hay {kind === "form" ? "envíos" : "respuestas"}. Sé el primero.</p> :
          kind === "poll" ? (
            <div className="space-y-3" data-testid="poll-results">{results.options.map((o) => <Bar key={o.label} {...o} />)}</div>
          ) : kind === "form" ? (
            <div className="overflow-x-auto border border-navy/10 rounded-lg" data-testid="form-results">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-navy/10 text-navy/50">{results.fields.map((c, i) => <th key={i} className="text-left font-medium px-3 py-2 whitespace-nowrap">{c}</th>)}</tr></thead>
                <tbody>
                  {results.submissions.map((s, ri) => (
                    <tr key={ri} className="border-b border-navy/5 last:border-0" data-testid={`form-sub-${ri}`}>
                      {results.fields.map((c, i) => <td key={i} className="px-3 py-2 text-navy/80 whitespace-nowrap">{s[i] || "—"}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-4" data-testid="survey-results">
              {results.questions.map((q, i) => (
                <div key={i} className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-navy/70 truncate pr-2">{q.label}</span><span className="shrink-0 font-editorial text-lg text-violet">{q.avg} <span className="text-xs text-navy/40">/ 5</span></span></div>
                  <div className="flex gap-1 items-end h-12">
                    {q.dist.map((c, n) => {
                      const max = Math.max(...q.dist, 1);
                      return <div key={n} className="flex-1 flex flex-col items-center gap-0.5">
                        <div className="w-full bg-violet/70 rounded-t" style={{ height: `${(c / max) * 100}%`, minHeight: c ? 4 : 0 }} title={`${n + 1}: ${c}`} />
                        <span className="text-[10px] text-navy/40">{n + 1}</span>
                      </div>;
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
